import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { BrowserStorage } from 'utils/browserStorage';
import { useMemo } from 'react';
import AppConfig from 'app/config/app';
import { PostCommunicationContent } from 'app/modules/posts/modules/post/components/postCommunication/types/postCommunicationContent';

type UnsavedValueDraft = {
  value: string;
  key: (string | number)[] | string;
};

type UnsavedValue = {
  createdAt: string;
  value: string;
  key: string;
};

function getUnsavedValueKey(key: UnsavedValueDraft['key']) {
  return JSON.stringify(key);
}

function createUnsavedValue(draft: UnsavedValueDraft): UnsavedValue {
  return {
    key: getUnsavedValueKey(draft.key),
    createdAt: new Date().toJSON(),
    value: draft.value,
  };
}

type UnsavedValuesStore = {
  values: Record<string, UnsavedValue>;
  add(unsavedValueDraft: UnsavedValueDraft): void;
  get(key: UnsavedValueDraft['key']): UnsavedValue | undefined;
  remove(key: UnsavedValueDraft['key']): void;
  clearOld(): void;
};

const useUnsavedValuesStore = create<UnsavedValuesStore>()(
  persist(
    (set, get) => ({
      values: {},
      add(unsavedValueDraft: UnsavedValueDraft) {
        get().clearOld();

        set(({ values }) => {
          const unsavedValue = createUnsavedValue(unsavedValueDraft);

          return {
            values: {
              ...values,
              [unsavedValue.key]: unsavedValue,
            },
          };
        });
      },
      get(key: UnsavedValueDraft['key']): UnsavedValue | undefined {
        return get().values[getUnsavedValueKey(key)];
      },
      remove(key: UnsavedValueDraft['key']): void {
        const values = get().values;

        delete values[getUnsavedValueKey(key)];

        set({ values });
      },
      clearOld() {
        const values = get().values;

        if (Object.keys(values).length < AppConfig.MAX_UNSAVED_LIMIT_TO_STORE) {
          return;
        }

        const newValues = Object.fromEntries(
          Object.entries(values)
            .sort(
              ([aKey, a], [bKey, b]) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )
            .slice(0, AppConfig.MAX_UNSAVED_LIMIT_TO_STORE),
        );

        set({ values: newValues });
      },
    }),
    {
      name: BrowserStorage.keys.Stores.UnsavedValues,
    },
  ),
);

export function useUnsavedValues() {
  const { add, get, remove } = useUnsavedValuesStore();

  return useMemo(() => ({ add, get, remove }), [add, get, remove]);
}

export const UnsavedValuesKeys = {
  commentInputOfPost: (params: {
    postId: number;
    type: PostCommunicationContent;
  }) =>
    getUnsavedValueKey(['comment-input-of-post', params.postId, params.type]),
};
